import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import ArrowDropDown from "~icons/material-symbols/arrow-drop-down.svg";
import Close from "~icons/material-symbols/close.svg";
import Reload from "~icons/material-symbols/refresh.svg";
import Search from "~icons/material-symbols/search.svg";

import { css } from "../../../../styled-system/css";
import { LoadingOverlay } from "../../../components/loading-overlay";
import {
  type DistributionCodeSearchResult,
  type DistributionCodeSearchTarget,
  useGetAllFundSources,
  useSearchDistributionCodes,
} from "../../../libs/admin/hooks";
import { formatTimestampJaJst } from "../../../libs/utils/format-timestamp";
import { button } from "../../../styles/buttonStyles";
import { contentWrapper } from "../../../styles/contentWrapper";
import {
  input,
  inputButton,
  inputWithButtonContainer,
} from "../../../styles/inputStyles";
import { pageTitle, titleWrapper } from "../../../styles/sectionStyles";
import {
  table,
  tableCell,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
} from "../../../styles/tableStyles";
import { getDistributionCodeSearchTarget, isValidCodeSearch } from "./-search";

export const Route = createFileRoute("/admin/distribution-codes/")({
  component: RouteComponent,
});

type SearchCondition = {
  target: DistributionCodeSearchTarget;
  fundSourceId: string;
  code: string;
};

const Toolbar = (props: {
  fundSources: { id: string; name: string; type: string }[];
  onReset: () => void;
  onSubmit: (condition: SearchCondition) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fundSourceId, setFundSourceId] = useState("");
  const [code, setCode] = useState("");
  const selectedFundSource = props.fundSources.find(
    (fundSource) => fundSource.id === fundSourceId,
  );
  const target = getDistributionCodeSearchTarget(selectedFundSource?.type);
  const canSubmit = target ? isValidCodeSearch(target, code) : false;

  const reset = () => {
    setCode("");
    props.onReset();
  };

  return (
    <div
      className={css({ display: "flex", flexDirection: "column", gap: "8px" })}
    >
      <form
        className={css({
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "16px",
          alignItems: "end",
        })}
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit && target) {
            props.onSubmit({ target, fundSourceId, code: code.trim() });
          }
        }}
        onReset={(event) => {
          event.preventDefault();
          reset();
        }}
      >
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            width: "240px",
          })}
        >
          <div className={css({ fontSize: "12px" })}>検索対象</div>
          <div className={inputWithButtonContainer()}>
            <select
              className={css(input.raw(), {
                appearance: "none",
                paddingRight: "36px",
              })}
              value={fundSourceId}
              onChange={(event) => {
                setFundSourceId(event.target.value);
                setCode("");
                props.onReset();
              }}
            >
              <option value="">選択してください</option>
              {props.fundSources.map((fundSource) => (
                <option key={fundSource.id} value={fundSource.id}>
                  {fundSource.name}
                </option>
              ))}
            </select>
            <ArrowDropDown
              className={css({
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: "text.secondary",
              })}
            />
          </div>
        </div>

        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            width: "400px",
          })}
        >
          <div className={css({ fontSize: "12px" })}>
            {target === "distribution_codes" ? "コード先頭8桁" : "コード"}
          </div>
          <div className={inputWithButtonContainer()}>
            <input
              ref={inputRef}
              type="text"
              className={input()}
              disabled={!target}
              placeholder={
                target === "distribution_codes"
                  ? "a1b2c3d4"
                  : target === "standalone_distribution_codes"
                    ? "Ab3Cd4Ef5Gh6"
                    : ""
              }
              maxLength={target === "distribution_codes" ? 8 : undefined}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <button
              type="button"
              className={inputButton({ semantic: "danger" })}
              disabled={code === ""}
              onClick={() => {
                setCode("");
                inputRef.current?.focus();
              }}
            >
              <Close />
            </button>
          </div>
        </div>

        <button type="reset" className={button()}>
          <Reload />
          リセット
        </button>
        <button
          type="submit"
          className={button({ color: "primary" })}
          disabled={!canSubmit}
        >
          <Search />
          検索
        </button>
      </form>
    </div>
  );
};

export const ResultsTable = (props: {
  rows: DistributionCodeSearchResult[];
  isFetching: boolean;
  hasSearched: boolean;
  error: Error | null;
}) => (
  <div className={tableWrapper()}>
    {props.isFetching && <LoadingOverlay />}
    {props.error && (
      <div
        role="alert"
        className={css({ fontSize: "14px", color: "text.accentError" })}
      >
        検索に失敗しました。時間を置いて再度お試しください。
      </div>
    )}
    <table className={table()}>
      <thead
        className={tableHead({
          disabled: props.error !== null || props.rows.length === 0,
        })}
      >
        <tr>
          <th className={tableHeadCell()}>コード</th>
          <th className={tableHeadCell()}>使用者（subscription ID）</th>
          <th className={tableHeadCell()}>使用時間</th>
        </tr>
      </thead>
      <tbody>
        {!props.error &&
          props.rows.map((row) => (
            <tr key={row.code} className={tableRow()}>
              <td
                className={css(tableCell.raw(), {
                  fontFamily: "monospace",
                  whiteSpace: "nowrap",
                })}
              >
                {row.code}
              </td>
              <td className={tableCell()}>{row.subscriptionId ?? "--"}</td>
              <td className={tableCell()}>
                {row.usedAt
                  ? formatTimestampJaJst(row.usedAt, { withTime: true })
                  : "--"}
              </td>
            </tr>
          ))}
        {props.hasSearched &&
          !props.isFetching &&
          !props.error &&
          props.rows.length === 0 && (
            <tr className={tableRow()}>
              <td
                colSpan={3}
                className={css(tableCell.raw(), { textAlign: "center" })}
              >
                該当するコードがありません
              </td>
            </tr>
          )}
      </tbody>
    </table>
  </div>
);

function RouteComponent() {
  const queryClient = useQueryClient();
  const [condition, setCondition] = useState<SearchCondition>();
  const fundSourcesQuery = useGetAllFundSources();
  const fundSources = (fundSourcesQuery.data ?? []).filter(
    ({ type }) =>
      type === "Distributable" || type === "StandaloneDistributable",
  );
  const search = useSearchDistributionCodes({
    target: condition?.target ?? "distribution_codes",
    fundSourceId: condition?.fundSourceId,
    code: condition?.code,
  });

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>コード検索</h1>
      </div>
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        })}
      >
        <Toolbar
          fundSources={fundSources}
          onReset={() => setCondition(undefined)}
          onSubmit={(nextCondition) => {
            queryClient.invalidateQueries({
              queryKey: ["AdminSearchDistributionCodes"],
            });
            setCondition(nextCondition);
          }}
        />
        <ResultsTable
          rows={search.data ?? []}
          isFetching={search.isFetching}
          hasSearched={condition !== undefined}
          error={search.error}
        />
      </div>
    </div>
  );
}
